/**
 * Customer-page domain helpers (phase 3, decisions 70–76).
 *
 * Everything the PUBLIC surface shares between the token API, the PDF renderer
 * and the customer page lives here — most importantly the public line shape,
 * which is how buy/list prices stay off the wire BY CONSTRUCTION rather than
 * by remembering to strip them in every handler.
 */

import { clampQty, sectionKey, type SelectionGroup } from './pricing.js'
import type { Optionality, QuoteLine, SelectionMap, BillingFrequency } from './types.js'

// ---------------------------------------------------------------------------
// Decline reasons (decision 76) — structured picklist + optional free text.
// The keys feed phase-5 win/loss analytics and phase 4's HubSpot closed-lost
// reason, so they are stable identifiers, not display strings.
// ---------------------------------------------------------------------------

export const DECLINE_REASONS = [
  { key: 'too_expensive',     label: 'Too expensive' },
  { key: 'another_supplier',  label: 'Went with another supplier' },
  { key: 'timing_budget',     label: 'Timing / budget cycle' },
  { key: 'no_longer_needed',  label: 'No longer needed' },
  { key: 'other',             label: 'Other' },
] as const

export type DeclineReasonKey = (typeof DECLINE_REASONS)[number]['key']

export function isDeclineReason(key: unknown): key is DeclineReasonKey {
  return DECLINE_REASONS.some((r) => r.key === key)
}

// ---------------------------------------------------------------------------
// Public line shape (spec §8 + decision 53).
//
// ⚠️ The customer sees the SELL price only. `listPrice` would expose the gap a
// reseller is charging over Wave9's list, and `buyPrice` is the tier ladder /
// unit cost itself. Neither field EXISTS on this type — a payload built from
// PublicLine cannot leak them.
// ---------------------------------------------------------------------------

export interface PublicLine {
  id: string
  groupId: string | null
  name: string
  description: string | null
  qty: number
  qtyEditable: boolean
  qtyMin: number | null
  qtyMax: number | null
  subjectToSurvey: boolean
  /** The customer's unit price — the frozen SELL price, nothing else. */
  unitPrice: number
  recurring: boolean
  billingFrequency: BillingFrequency
  termMonths: number | null
  optionality: Optionality
  preselected: boolean
  sort: number
}

export function toPublicLine(line: QuoteLine): PublicLine {
  return {
    id: line.id,
    groupId: line.groupId,
    name: line.name,
    description: line.description ?? null,
    qty: line.qty,
    qtyEditable: line.qtyEditable,
    qtyMin: line.qtyMin,
    qtyMax: line.qtyMax,
    subjectToSurvey: line.subjectToSurvey,
    unitPrice: line.sellPrice,
    recurring: line.recurring,
    billingFrequency: line.billingFrequency ?? (line.recurring ? 'annually' : 'one_time'),
    termMonths: line.termMonths,
    optionality: line.optionality,
    preselected: line.preselected,
    sort: line.sort,
  }
}

/**
 * Rebuild QuoteLine-shaped objects from public lines so the browser can run the
 * SAME totals maths (`computeTotals` + `publicTotals`) the server runs. Buy is
 * pinned to 0 — the buy-side outputs are meaningless here and are never read.
 */
export function publicLineToQuoteLine(l: PublicLine): QuoteLine {
  return {
    id: l.id, groupId: l.groupId, hsProductId: null, name: l.name,
    description: l.description, qty: l.qty, qtyEditable: l.qtyEditable,
    qtyMin: l.qtyMin, qtyMax: l.qtyMax, subjectToSurvey: l.subjectToSurvey,
    isAdhoc: false, listPrice: 0, buyPrice: 0, sellPrice: l.unitPrice,
    recurring: l.recurring, billingFrequency: l.billingFrequency,
    termMonths: l.termMonths, optionality: l.optionality,
    preselected: l.preselected, sort: l.sort,
  }
}

// ---------------------------------------------------------------------------
// Selection sanitising (decision 71).
//
// The customer's browser proposes; the server disposes. Anything not on the
// quote, not selectable, or outside the qty bounds is corrected or dropped
// BEFORE it is stored — the stored map is always safe to feed straight into
// applySelections at acceptance time.
// ---------------------------------------------------------------------------

export function sanitiseSelections(
  lines: QuoteLine[], proposed: unknown, groups?: SelectionGroup[],
): SelectionMap {
  const out: SelectionMap = {}
  if (proposed == null || typeof proposed !== 'object' || Array.isArray(proposed)) return out
  const byId = new Map(lines.map((l) => [l.id, l]))
  // Optional sections (decision 87): their include/exclude state rides the same
  // map under `s:<groupId>` keys — accepted ONLY for groups actually optional.
  // Optional sections AND choice-set members (117) take s:<id> keys — a set
  // member is customer-selectable by construction, optional flag or not.
  const optionalSections = new Set((groups ?? []).filter((g) => g.kind === 'section' && (g.optional || g.choiceSet)).map((g) => sectionKey(g.id)))

  for (const [lineId, rawValue] of Object.entries(proposed as Record<string, unknown>)) {
    if (optionalSections.has(lineId)) {
      const v = rawValue as { selected?: unknown } | null
      if (v && typeof v === 'object' && typeof v.selected === 'boolean') out[lineId] = { selected: v.selected }
      continue
    }
    const line = byId.get(lineId)
    if (!line || rawValue == null || typeof rawValue !== 'object') continue
    const value = rawValue as { selected?: unknown; qty?: unknown }
    const entry: { selected?: boolean; qty?: number } = {}

    // Required lines are not deselectable; their `selected` is noise.
    if (line.optionality !== 'required' && typeof value.selected === 'boolean') {
      entry.selected = value.selected
    }
    if (line.qtyEditable && value.qty != null) {
      const requested = Number(value.qty)
      if (Number.isFinite(requested)) entry.qty = clampQty(line, requested)
    }
    if (entry.selected !== undefined || entry.qty !== undefined) out[lineId] = entry
  }
  return out
}

// ---------------------------------------------------------------------------
// Acceptance freeze shape (decisions 22 / 32 / 36 / 63).
// What goes into quote_acceptances.selections — the evidentiary record of
// exactly what was accepted, at what quantity, at what customer price.
// ---------------------------------------------------------------------------

export interface FrozenSelection {
  itemId: string
  name: string
  qty: number
  unitPrice: number
  recurring: boolean
  billingFrequency: BillingFrequency
  termMonths: number | null
  optionality: Optionality
  subjectToSurvey: boolean
}

export function freezeSelections(selectedLines: QuoteLine[]): FrozenSelection[] {
  return selectedLines.map((l) => ({
    itemId: l.id,
    name: l.name,
    qty: l.qty,
    unitPrice: l.sellPrice,
    recurring: l.recurring,
    billingFrequency: l.billingFrequency ?? (l.recurring ? 'annually' : 'one_time'),
    termMonths: l.termMonths,
    optionality: l.optionality,
    subjectToSurvey: l.subjectToSurvey,
  }))
}

// ---------------------------------------------------------------------------
// Decision 191: the frozen acceptance, read back as a SelectionMap so a staff
// surface (the builder's Customer preview) can render EXACTLY what the
// customer accepted through the same code path that renders the offer.
//
// Every line gets an explicit entry — accepted lines selected at the accepted
// quantity, everything else selected:false — so no preselected default can
// creep back in. A section (optional, or a choice-set member) counts as
// included when any accepted line sits in it or in one of its blocks.
// ---------------------------------------------------------------------------

export function selectionsFromAcceptance(
  frozen: ReadonlyArray<Pick<FrozenSelection, 'itemId' | 'qty'>>,
  lines: ReadonlyArray<Pick<QuoteLine, 'id' | 'groupId'>>,
  groups: ReadonlyArray<Pick<SelectionGroup, 'id' | 'kind' | 'parentGroupId' | 'optional' | 'choiceSet'>>,
): SelectionMap {
  const acceptedQty = new Map(frozen.map((f) => [f.itemId, f.qty]))
  const out: SelectionMap = {}
  const byId = new Map(groups.map((g) => [g.id, g]))
  const sectionHasAccepted = new Set<string>()
  for (const l of lines) {
    const q = acceptedQty.get(l.id)
    out[l.id] = q == null ? { selected: false } : { selected: true, qty: q }
    if (q == null || !l.groupId) continue
    const g = byId.get(l.groupId)
    const sectionId = g ? (g.kind === 'choose_one' ? (g.parentGroupId ?? null) : g.id) : null
    if (sectionId) sectionHasAccepted.add(sectionId)
  }
  for (const g of groups) {
    if (g.kind !== 'section' || g.parentGroupId) continue
    if (g.optional || g.choiceSet) out[sectionKey(g.id)] = { selected: sectionHasAccepted.has(g.id) }
  }
  return out
}

// ---------------------------------------------------------------------------
// Caveat fallbacks (decision 32) — the DB-seeded wording wins; these exist so
// a missing portal_settings row degrades to correct wording, never to none.
// ---------------------------------------------------------------------------

export const DEFAULT_CAVEAT_TEXT =
  'Acceptance of this quotation is subject to survey and technical due diligence. '
  + 'It is a commitment to proceed, not an unconditional contract; any variation identified '
  + 'by survey will be confirmed in a revised quotation before you are committed.'
export const DEFAULT_CAVEAT_VERSION = 'v1 (2026-08-05)'

/** Statuses a customer may still act on. Everything else renders read-only. */
export function isActionableStatus(status: string): boolean {
  return status === 'sent' || status === 'viewed'
}

// ---------------------------------------------------------------------------
// Authority statement (decision 82) — shown and REQUIRED when accepting with
// no purchase order; the exact wording freezes into quote_acceptances.
// One builder used by the accept form, the server and the confirmation email,
// so the statement confirmed is always the statement recorded.
// ---------------------------------------------------------------------------

export function authorityStatement(organisationName: string | null): string {
  const org = organisationName?.trim() || 'the purchasing organisation'
  return `I confirm that I am authorised to accept this quotation on behalf of ${org}, and that a purchase order reference will be provided if required by our procurement process.`
}

/**
 * Decision 218: the AMEND wording — confirmed when a customer changes the
 * selection on an already-accepted quotation through their amend link. The
 * exact statement freezes into the amended record, like decision 82's.
 */
export function amendAuthorityStatement(organisationName: string | null): string {
  const org = organisationName?.trim() || 'the purchasing organisation'
  return `I confirm that I am authorised to amend the accepted quotation on behalf of ${org}, that the selection I am confirming replaces the acceptance on record, and that a purchase order reference will be provided if required by our procurement process.`
}

/**
 * Decision 94 (2026-08-06): the on-behalf variant. Wave9 staff sometimes
 * record an acceptance the customer gave by phone/email/PO. The statement
 * names WHO at the customer gave the instruction and WHO at Wave9 recorded it
 * — the record must never read as if the customer clicked accept themselves.
 */
export function onBehalfAuthorityStatement(
  organisationName: string | null, contactName: string, staffName: string | null,
): string {
  const org = organisationName?.trim() || 'the purchasing organisation'
  const staff = staffName?.trim() || 'a Wave 9 staff member'
  const contact = contactName.trim() || 'the customer contact'
  return `Recorded by ${staff} (Wave 9) on the instruction of ${contact}, who confirmed they are authorised to accept this quotation on behalf of ${org}, and that a purchase order reference will be provided if required by their procurement process.`
}
